import React, { useState, useRef, Fragment, type ComponentType } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import { XIcon, SearchIcon, CheckIcon, UserPlusIcon, ChevronRightIcon, ChevronLeftIcon, ConstructionIcon, TreePineIcon, Building2Icon, TrashIcon, DropletIcon, LightbulbIcon, CarIcon, PawPrintIcon, MapPinIcon, CrosshairIcon, PaperclipIcon, FileIcon, UploadCloudIcon } from 'lucide-react';
interface CreateRequeteDialogProps {
  onClose: () => void;
}
const STEPS: Localized[] = [{
  fr: 'Demandeur',
  en: 'Requester'
}, {
  fr: 'Catégorie',
  en: 'Category'
}, {
  fr: 'Sous-catégorie',
  en: 'Subcategory'
}, {
  fr: 'Lieu',
  en: 'Location'
}, {
  fr: 'Détails',
  en: 'Details'
}];
const SAMPLE_USERS = [{
  name: 'LudikSearch BcitiTest',
  email: 'dogan.erisen+28032025@hotmail.com',
  phone: '(778) 927-6462',
  avatar: 'https://i.pravatar.cc/80?img=12'
}, {
  name: 'Sarah Connor',
  email: 'dogan.erisen@hotmail.com',
  phone: '(111) 111-1111',
  avatar: 'https://i.pravatar.cc/80?img=45'
}, {
  name: 'Belanor Waesceran',
  email: 'belanor.w@hotmail.com',
  phone: '(514) 555-0199',
  avatar: 'https://i.pravatar.cc/80?img=33'
}];
type Localized = {
  fr: string;
  en: string;
};
interface Category {
  id: string;
  name: Localized;
  desc: Localized;
  icon: ComponentType<{
    className?: string;
  }>;
  internal?: boolean;
  subcategories: Localized[];
}
const CATEGORIES: Category[] = [{
  id: 'voirie',
  name: {
    fr: 'Voirie et infrastructures routières',
    en: 'Roads and road infrastructure'
  },
  desc: {
    fr: 'Nids-de-poule, trottoirs, signalisation et chaussée',
    en: 'Potholes, sidewalks, signage and pavement'
  },
  icon: ConstructionIcon,
  subcategories: [{
    fr: 'Nid-de-poule',
    en: 'Pothole'
  }, {
    fr: 'Trottoir endommagé',
    en: 'Damaged sidewalk'
  }, {
    fr: 'Affaissement de chaussée',
    en: 'Road subsidence'
  }, {
    fr: 'Marquage au sol effacé',
    en: 'Faded road markings'
  }, {
    fr: 'Bordure de rue à réparer',
    en: 'Curb needing repair'
  }]
}, {
  id: 'parcs',
  name: {
    fr: 'Parcs et espaces récréatifs',
    en: 'Parks and recreational spaces'
  },
  desc: {
    fr: 'Espaces verts, mobilier urbain et aires de jeux',
    en: 'Green spaces, street furniture and playgrounds'
  },
  icon: TreePineIcon,
  subcategories: [{
    fr: 'Jeux pour enfants endommagés',
    en: 'Damaged play equipment'
  }, {
    fr: 'Mobilier de parc à réparer',
    en: 'Park furniture needing repair'
  }, {
    fr: 'Entretien des espaces verts',
    en: 'Green space maintenance'
  }, {
    fr: 'Arbre dangereux ou tombé',
    en: 'Dangerous or fallen tree'
  }, {
    fr: 'Terrain sportif à entretenir',
    en: 'Sports field maintenance'
  }]
}, {
  id: 'eclairage',
  name: {
    fr: 'Éclairage public',
    en: 'Street lighting'
  },
  desc: {
    fr: 'Lampadaires et feux de circulation',
    en: 'Streetlights and traffic lights'
  },
  icon: LightbulbIcon,
  subcategories: [{
    fr: 'Lampadaire défectueux',
    en: 'Faulty streetlight'
  }, {
    fr: 'Feu de circulation en panne',
    en: 'Traffic light out of order'
  }, {
    fr: 'Éclairage de parc défectueux',
    en: 'Faulty park lighting'
  }]
}, {
  id: 'eau',
  name: {
    fr: 'Aqueduc et égouts',
    en: 'Water and sewers'
  },
  desc: {
    fr: 'Fuites, refoulements et bornes-fontaines',
    en: 'Leaks, backups and fire hydrants'
  },
  icon: DropletIcon,
  subcategories: [{
    fr: "Fuite d'eau sur la voie publique",
    en: 'Water leak on public road'
  }, {
    fr: "Refoulement d'égout",
    en: 'Sewer backup'
  }, {
    fr: 'Borne-fontaine endommagée',
    en: 'Damaged fire hydrant'
  }, {
    fr: "Problème de qualité de l'eau",
    en: 'Water quality issue'
  }]
}, {
  id: 'collecte',
  name: {
    fr: 'Collecte et propreté',
    en: 'Collection and cleanliness'
  },
  desc: {
    fr: 'Déchets, recyclage et propreté urbaine',
    en: 'Waste, recycling and urban cleanliness'
  },
  icon: TrashIcon,
  subcategories: [{
    fr: 'Collecte manquée',
    en: 'Missed collection'
  }, {
    fr: 'Bac de recyclage endommagé',
    en: 'Damaged recycling bin'
  }, {
    fr: 'Dépôt sauvage de déchets',
    en: 'Illegal dumping'
  }, {
    fr: 'Conteneur à réparer',
    en: 'Container needing repair'
  }]
}, {
  id: 'stationnement',
  name: {
    fr: 'Stationnement et circulation',
    en: 'Parking and traffic'
  },
  desc: {
    fr: 'Stationnement, signalisation et déneigement',
    en: 'Parking, signage and snow removal'
  },
  icon: CarIcon,
  subcategories: [{
    fr: 'Stationnement illégal',
    en: 'Illegal parking'
  }, {
    fr: 'Déneigement à effectuer',
    en: 'Snow removal needed'
  }, {
    fr: 'Panneau de signalisation manquant',
    en: 'Missing road sign'
  }]
}, {
  id: 'batiment',
  name: {
    fr: 'Bâtiment et urbanisme',
    en: 'Building and urban planning'
  },
  desc: {
    fr: 'Permis, salubrité et nuisances',
    en: 'Permits, sanitation and nuisances'
  },
  icon: Building2Icon,
  internal: true,
  subcategories: [{
    fr: 'Construction sans permis',
    en: 'Construction without permit'
  }, {
    fr: 'Bâtiment insalubre',
    en: 'Unsanitary building'
  }, {
    fr: 'Nuisance sonore',
    en: 'Noise nuisance'
  }, {
    fr: 'Infraction au zonage',
    en: 'Zoning violation'
  }]
}, {
  id: 'animaux',
  name: {
    fr: 'Animaux',
    en: 'Animals'
  },
  desc: {
    fr: 'Animaux errants, nuisances et contrôle animalier',
    en: 'Stray animals, nuisances and animal control'
  },
  icon: PawPrintIcon,
  subcategories: [{
    fr: 'Animal errant',
    en: 'Stray animal'
  }, {
    fr: 'Nuisance animale',
    en: 'Animal nuisance'
  }, {
    fr: 'Animal mort sur la voie publique',
    en: 'Dead animal on public road'
  }]
}];
const mapMarkerIcon = L.divIcon({
  className: '',
  html: `<div style="width:20px;height:20px;border-radius:9999px;background:#4F46E5;border:3px solid white;box-shadow:0 0 0 3px rgba(79,70,229,0.3);"></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10]
});
type LocationMode = 'geo' | 'address' | 'none';
export function CreateRequeteDialog({
  onClose
}: CreateRequeteDialogProps) {
  const {
    t,
    lang
  } = useLanguage();
  const [step, setStep] = useState(0);
  const [query, setQuery] = useState('');
  const [selectedRequester, setSelectedRequester] = useState('Hugo Langlais');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSubcat, setSelectedSubcat] = useState<Localized | null>(null);
  const [locationMode, setLocationMode] = useState<LocationMode>('geo');
  const [detailTitle, setDetailTitle] = useState('');
  const [detailDesc, setDetailDesc] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const category = CATEGORIES.find(c => c.id === selectedCategory);
  const subcategories = category?.subcategories ?? [];
  const handleFiles = (files: FileList | null) => {
    if (!files) return;
    setAttachments(prev => [...prev, ...Array.from(files)]);
  };
  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };
  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} ${t('o', 'B')}`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} ${t('Ko', 'KB')}`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} ${t('Mo', 'MB')}`;
  };
  const canContinue = () => {
    if (step === 0) return !!selectedRequester;
    if (step === 1) return !!selectedCategory;
    if (step === 2) return !!selectedSubcat;
    return true;
  };
  const next = () => {
    if (step < STEPS.length - 1) setStep(step + 1);else onClose();
  };
  const prev = () => setStep(Math.max(0, step - 1));
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1880" data-mp="m2zbrekz54ly">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} data-id="element-1881" data-mp="m0whujgparby"></div>
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col animate-in zoom-in-95 duration-200" data-id="element-1882" data-mp="3rqh6jdewu0du">
        {/* Header */}
        <div className="relative px-8 pt-6 pb-2 flex-shrink-0" data-id="element-1883" data-mp="m0miki1w7lb">
          <button onClick={onClose} aria-label={t('Fermer', 'Close')} className="absolute top-5 right-5 p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100" data-id="element-1884" data-mp="5i7t8fi9tn4b">
            <XIcon className="w-5 h-5" data-id="element-1885" data-mp="ejfgm2j913jm" />
          </button>
          <h2 className="text-center text-lg font-bold text-[#0F172A]" data-id="element-1886" data-mp="ji0fnymrlh6h">
            {t('Créer une requête', 'Create a request')}
          </h2>
        </div>

        {/* Stepper */}
        <div className="px-8 py-5 flex-shrink-0" data-id="element-1887" data-mp="t94umycfx21b">
          <div className="flex items-center" data-id="element-1888" data-mp="t7tqgxf31s9o">
            {STEPS.map((label, i) => {
            const completed = i < step;
            const current = i === step;
            return <Fragment key={label.fr} data-id="element-1889" data-mp="129tck44yfmjh" data-mpi={i}>
                  <div className="flex flex-col items-center gap-2 relative" data-id="element-1890" data-mp="2mr2grh6bc5y6">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors ${completed ? 'bg-[#4F46E5] border-[#4F46E5] text-white' : current ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-slate-300 text-slate-300'}`} data-id="element-1891" data-mp="2mozgm4bg4wfq">
                      {completed ? <CheckIcon className="w-4 h-4" data-id="element-1892" data-mp="36fkzph0q6n4f" /> : <span className={`w-2 h-2 rounded-full ${current ? 'bg-[#4F46E5]' : 'bg-slate-300'}`} data-id="element-1893" data-mp="31fouxfk7ernt"></span>}
                    </div>
                    <span className={`text-xs font-semibold whitespace-nowrap ${current || completed ? 'text-[#0F172A]' : 'text-slate-400'}`} data-id="element-1894" data-mp="2rpdptposacfc">
                      {label[lang]}
                    </span>
                  </div>
                  {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 -mt-6 ${i < step ? 'bg-[#4F46E5]' : 'bg-slate-200'}`} data-id="element-1895" data-mp="3n068llf2uul"></div>}
                </Fragment>;
          })}
          </div>
        </div>

        {/* Content */}
        <div className="px-8 py-4 overflow-y-auto flex-1" data-id="element-1896" data-mp="8kji5eun6vjd">
          {/* STEP 0 — Demandeur */}
          {step === 0 && <div data-id="element-1897" data-mp="mjj8qs1ptki6">
              <h3 className="text-xl font-bold text-[#0F172A] mb-2" data-id="element-1898" data-mp="mizo33koki0g">
                {t('Sélectionner le demandeur', 'Select the requester')}
              </h3>
              <p className="text-sm text-slate-500 mb-6" data-id="element-1899" data-mp="w9udggt83xq4">
                {t('Recherchez le citoyen qui effectue la demande ou enregistrez un nouveau citoyen', 'Search for the citizen making the request or register a new citizen')}
              </p>

              <button onClick={() => setSelectedRequester('Hugo Langlais')} className={`w-full flex items-center gap-4 p-4 rounded-xl border text-left transition-colors ${selectedRequester === 'Hugo Langlais' ? 'border-[#0F172A]' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-1900" data-mp="11bth7rsvkwnl">
                <div className="w-12 h-12 rounded-full bg-[#0F172A] flex items-center justify-center text-white text-xs font-bold flex-shrink-0" data-id="element-1901" data-mp="1azytzr0plu0k">
                  bciti+
                </div>
                <div data-id="element-1902" data-mp="115yd6rw2f0c0">
                  <div className="flex items-center gap-2" data-id="element-1903" data-mp="15yrjhk76mgq7">
                    <span className="font-bold text-slate-900" data-id="element-1904" data-mp="160cgrhkvsdrr">
                      Hugo Langlais
                    </span>
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-indigo-50 text-[#4F46E5]" data-id="element-1905" data-mp="s5jrhrf6vj7z">
                      {t('Moi', 'Me')}
                    </span>
                  </div>
                  <div className="text-sm text-slate-500" data-id="element-1906" data-mp="ibjez4hkvbp8">
                    hugo.langlais@bciti.com
                  </div>
                  <div className="text-sm text-slate-500" data-id="element-1907" data-mp="2oxz7rxxjrvoe">(111) 111-1111</div>
                </div>
              </button>

              <div className="flex items-center gap-3 my-6" data-id="element-1908" data-mp="2k5o5iofc3xso">
                <div className="flex-1 h-px bg-slate-200" data-id="element-1909" data-mp="2g55fmzx051t4"></div>
                <span className="text-xs text-slate-400" data-id="element-1910" data-mp="2g5dp0htx1ozk">
                  {t('ou rechercher un autre utilisateur', 'or search for another user')}
                </span>
                <div className="flex-1 h-px bg-slate-200" data-id="element-1911" data-mp="37va6nhf9on3b"></div>
              </div>

              <div className="relative" data-id="element-1912" data-mp="33ughkkq3928u">
                <SearchIcon className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" data-id="element-1913" data-mp="33vhs8o36zmq1" />
                <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder={t('Rechercher un utilisateur', 'Search for a user')} className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-1914" data-mp="2yxq2o0q0scp0" />
              </div>

              {query.length > 0 && <div className="mt-2 border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100 shadow-sm" data-id="element-1915" data-mp="1dd1fytq2sy3q">
                  <button className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 text-left" data-id="element-1916" data-mp="1i9rx7kdm9zqq">
                    <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-1917" data-mp="1n45z85msb54d">
                      <UserPlusIcon className="w-4 h-4 text-slate-500" data-id="element-1918" data-mp="1w2re9qt1s20e" />
                    </div>
                    <div data-id="element-1919" data-mp="212dqbwx53c7m">
                      <div className="text-sm font-semibold text-slate-900" data-id="element-1920" data-mp="2135n3b2h3fzh">
                        {t('Créer un nouveau compte', 'Create a new account')}
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-1921" data-mp="6ouvwvyeo9qj">
                        {t('Enregistrer un nouveau citoyen', 'Register a new citizen')}
                      </div>
                    </div>
                  </button>
                  {SAMPLE_USERS.map(u => <button key={u.email} onClick={() => {
              setSelectedRequester(u.name);
              setQuery('');
            }} className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 text-left" data-id="element-1922" data-mp="1x56tg5fdeyrg">
                      <img src={u.avatar} alt={u.name} className="w-9 h-9 rounded-full object-cover flex-shrink-0" data-id="element-1923" data-mp="1ihc1wsd3n0fy" />
                      <div data-id="element-1924" data-mp="1s6vjzvszmq9r">
                        <div className="text-sm font-semibold text-slate-900" data-id="element-1925" data-mp="1s86mlsap5rza">
                          {u.name}
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-1926" data-mp="zp12x9lkfmn4">{u.email}</div>
                        <div className="text-xs text-slate-500" data-id="element-1927" data-mp="uqzkmqvyrrcp">{u.phone}</div>
                      </div>
                    </button>)}
                </div>}
            </div>}

          {/* STEP 1 — Catégorie */}
          {step === 1 && <div data-id="element-1928" data-mp="2xddc5ca41jx4">
              <h3 className="text-lg font-bold text-[#0F172A] mb-1" data-id="element-1929" data-mp="2xdwv8wbhyecq">
                {t('Catégorie de requête', 'Request category')}
              </h3>
              <p className="text-sm text-slate-500 mb-4" data-id="element-1930" data-mp="32cpyzx0zeqzp">
                {t('Sélectionnez la catégorie qui décrit le mieux votre demande :', 'Select the category that best describes your request:')}
              </p>
              <div className="relative mb-4" data-id="element-1931" data-mp="3g2tgkaox1wit">
                <SearchIcon className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" data-id="element-1932" data-mp="3g68tb6v4ath2" />
                <input type="text" placeholder={t('Rechercher une catégorie', 'Search for a category')} className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-1933" data-mp="24opkmyyrpyb4" />
              </div>
              <div className="space-y-3" data-id="element-1934" data-mp="1lldzqwtzdd5l">
                {CATEGORIES.map(c => {
              const Icon = c.icon;
              const selected = selectedCategory === c.id;
              return <button key={c.id} onClick={() => {
                setSelectedCategory(c.id);
                setSelectedSubcat(null);
              }} className={`w-full text-left p-4 rounded-xl border transition-colors flex items-start gap-3 ${selected ? 'border-[#4F46E5] border-l-4 bg-indigo-50/30' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-1935" data-mp="1bu17w586mmzk">
                      <Icon className="w-6 h-6 text-slate-700 flex-shrink-0 mt-0.5" data-id="element-1936" data-mp="2s8sz4etldo7a" />
                      <div data-id="element-1937" data-mp="2n8wzerrxgixj">
                        <div className="flex items-center gap-2" data-id="element-1938" data-mp="2narhzzfq7yyg">
                          <span className="font-bold text-slate-900" data-id="element-1939" data-mp="2utha8e6epdaj">
                            {c.name[lang]}
                          </span>
                          {c.internal && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 text-[#4F46E5] border border-indigo-100" data-id="element-1940" data-mp="34j0vfqhldj8k">
                              {t('INTERNE', 'INTERNAL')}
                            </span>}
                        </div>
                        <p className="text-sm text-slate-500 mt-1" data-id="element-1941" data-mp="2gsy5bfx8mncl">{c.desc[lang]}</p>
                      </div>
                    </button>;
            })}
              </div>
            </div>}

          {/* STEP 2 — Sous-catégorie */}
          {step === 2 && <div data-id="element-1942" data-mp="1225x0vdt2mp">
              <h3 className="text-sm font-bold text-[#0F172A] mb-2" data-id="element-1943" data-mp="12le3pv8ccnj">
                {t('Catégorie de requête', 'Request category')}
              </h3>
              <div className="p-4 rounded-xl border border-slate-200 flex items-center gap-3 mb-6" data-id="element-1944" data-mp="55qenmvr249j">
                {category && <category.icon className="w-6 h-6 text-slate-700" data-id="element-1945" data-mp="m6cq5dqk3pai" />}
                <span className="font-bold text-slate-900" data-id="element-1946" data-mp="10v0fh29t72s0">
                  {category?.name[lang]}
                </span>
              </div>
              <h3 className="text-base font-bold text-[#0F172A] mb-3" data-id="element-1947" data-mp="w38y9icjm9ej">
                {t('Sélectionner une sous-catégorie', 'Select a subcategory')}
              </h3>
              <div className="relative mb-3" data-id="element-1948" data-mp="14wuc8zgvvmmw">
                <SearchIcon className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" data-id="element-1949" data-mp="14w2h1pr77qx5" />
                <input type="text" placeholder={t('Rechercher le sujet de votre requête', 'Search for your request topic')} className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-1950" data-mp="1jt3b3zwv3xys" />
              </div>
              <div className="space-y-2" data-id="element-1951" data-mp="1w55epn37nmlo">
                {subcategories.map(s => {
              const selected = selectedSubcat?.fr === s.fr;
              return <button key={s.fr} onClick={() => setSelectedSubcat(s)} className={`w-full text-left px-4 py-3 rounded-xl border font-medium text-slate-800 transition-colors ${selected ? 'border-[#0F172A]' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-1952" data-mp="1messzrjntndy">
                      {s[lang]}
                    </button>;
            })}
              </div>
            </div>}

          {/* STEP 3 — Lieu */}
          {step === 3 && <div data-id="element-1953" data-mp="18gm5n5cc75sj">
              <h3 className="text-sm font-bold text-[#0F172A] mb-2" data-id="element-1954" data-mp="2xtg7mm7a8e0q">
                {t('Emplacement de la requête', 'Request location')}
              </h3>
              <div className="p-4 rounded-xl border border-slate-200 mb-4" data-id="element-1955" data-mp="32r7v940w9dz4">
                <div className="flex items-center gap-3" data-id="element-1956" data-mp="a72t6s1w8bpf">
                  {category && <category.icon className="w-6 h-6 text-slate-700" data-id="element-1957" data-mp="eyb5pwyisd6t" />}
                  <span className="font-bold text-slate-900" data-id="element-1958" data-mp="176d3etkkl5z">
                    {category?.name[lang]}
                  </span>
                </div>
                <p className="text-sm text-slate-400 mt-1 ml-9" data-id="element-1959" data-mp="58hsysmn5s0z">
                  {selectedSubcat?.[lang]}
                </p>
              </div>
              <div className="h-56 rounded-xl overflow-hidden border border-slate-200 mb-5" data-id="element-1960" data-mp="xxfct06i3wvu">
                <MapContainer center={[45.4499, -73.4653]} zoom={13} scrollWheelZoom={false} style={{
              height: '100%',
              width: '100%'
            }} data-id="element-1961" data-mp="jxe8tbuxw9c5">
                  <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-1962" data-mp="39mgcug9zleal" />
                  <Marker position={[45.4499, -73.4653]} icon={mapMarkerIcon} data-id="element-1963" data-mp="2x2sritwrdli8" />
                </MapContainer>
              </div>
              <h3 className="text-base font-bold text-[#0F172A] mb-1" data-id="element-1964" data-mp="3b2u4h7qasje2">
                {t('Localiser votre requête', 'Locate your request')}
              </h3>
              <p className="text-sm text-slate-500 mb-3" data-id="element-1965" data-mp="2ie6e4xqoz3tn">
                {t('Utilisez votre position actuelle, entrez une adresse ou continuez sans adresse', 'Use your current location, enter an address or continue without an address')}
              </p>

              {/* Geolocation — default option */}
              <button onClick={() => setLocationMode('geo')} className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-colors mb-3 ${locationMode === 'geo' ? 'border-[#4F46E5] bg-indigo-50/30' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-1966" data-mp="2nc7u39stmn0w">
                <span className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 ${locationMode === 'geo' ? 'border-[#4F46E5]' : 'border-slate-300'}`} data-id="element-1967" data-mp="3l22b8iorwmj">
                  {locationMode === 'geo' && <span className="w-2 h-2 rounded-full bg-[#4F46E5]" data-id="element-1968" data-mp="ccucdi3st3ql"></span>}
                </span>
                <CrosshairIcon className="w-5 h-5 text-[#4F46E5] flex-shrink-0" data-id="element-1969" data-mp="3gru1uupe4kaj" />
                <span data-id="element-1970" data-mp="3gty3t73qb986">
                  <span className="flex items-center gap-2" data-id="element-1971" data-mp="3ktfd6bma0qim">
                    <span className="block text-sm font-semibold text-slate-800" data-id="element-1972" data-mp="3ks46a2xjb2na">
                      {t('Utiliser ma géolocalisation', 'Use my location')}
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 text-[#4F46E5] border border-indigo-100" data-id="element-1973" data-mp="102etdvge5qx8">
                      {t('RECOMMANDÉ', 'RECOMMENDED')}
                    </span>
                  </span>
                  <span className="block text-xs text-slate-500" data-id="element-1974" data-mp="1a74yubb07d67">
                    {t('Détecter automatiquement votre position actuelle', 'Automatically detect your current position')}
                  </span>
                </span>
              </button>

              {locationMode === 'geo' && <div className="flex items-center gap-3 p-3 rounded-xl bg-indigo-50/50 border border-indigo-100 mb-3" data-id="element-1975" data-mp="wfqg85l0ddzo">
                  <MapPinIcon className="w-5 h-5 text-[#4F46E5] flex-shrink-0" data-id="element-1976" data-mp="rg3zhm5z1fm4" />
                  <div className="text-sm" data-id="element-1977" data-mp="mhanjy6c1wo2">
                    <p className="font-semibold text-slate-800" data-id="element-1978" data-mp="mic0672zyr7w">
                      {t('Position détectée', 'Position detected')}
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-1979" data-mp="2gvbjobai05kg">
                      45.5672, -71.9940 · Windsor, QC
                    </p>
                  </div>
                </div>}

              {/* Address option */}
              <button onClick={() => setLocationMode('address')} className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-colors mb-3 ${locationMode === 'address' ? 'border-[#4F46E5] bg-indigo-50/30' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-1980" data-mp="2jy7whohs1xeh">
                <span className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 ${locationMode === 'address' ? 'border-[#4F46E5]' : 'border-slate-300'}`} data-id="element-1981" data-mp="2ewaa4le2ymhb">
                  {locationMode === 'address' && <span className="w-2 h-2 rounded-full bg-[#4F46E5]" data-id="element-1982" data-mp="1r9kbzv4urc1z"></span>}
                </span>
                <MapPinIcon className="w-5 h-5 text-slate-500 flex-shrink-0" data-id="element-1983" data-mp="1w7vibvvxv4fg" />
                <span data-id="element-1984" data-mp="1i95l8g94d4r3">
                  <span className="block text-sm font-semibold text-slate-800" data-id="element-1985" data-mp="1i8mcnq5czspq">
                    {t('Entrer une adresse', 'Enter an address')}
                  </span>
                  <span className="block text-xs text-slate-500" data-id="element-1986" data-mp="13m6uyom00gdy">
                    {t("Rechercher l'adresse la plus proche de votre requête", 'Search for the address closest to your request')}
                  </span>
                </span>
              </button>

              {locationMode === 'address' && <div className="relative mb-3" data-id="element-1987" data-mp="18inknn8uj2t5">
                  <SearchIcon className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" data-id="element-1988" data-mp="1mjq7w3bkuj5n" />
                  <input type="text" placeholder={t("Rechercher l'adresse la plus proche pour votre requête", 'Search for the closest address to your request')} className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-1989" data-mp="1hlexnrhde6mw" />
                </div>}

              {/* No address option */}
              <button onClick={() => setLocationMode('none')} className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-colors ${locationMode === 'none' ? 'border-[#4F46E5] bg-indigo-50/30' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-1990" data-mp="209jzgtegvhj2">
                <span className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 ${locationMode === 'none' ? 'border-[#4F46E5]' : 'border-slate-300'}`} data-id="element-1991" data-mp="1bp5001itstb0">
                  {locationMode === 'none' && <span className="w-2 h-2 rounded-full bg-[#4F46E5]" data-id="element-1992" data-mp="1lpnrnigmte3h"></span>}
                </span>
                <span data-id="element-1993" data-mp="1uo94cwzf9ywc">
                  <span className="block text-sm font-semibold text-slate-800" data-id="element-1994" data-mp="1uor7mdki5ddr">
                    {t('Sans adresse', 'No address')}
                  </span>
                  <span className="block text-xs text-slate-500" data-id="element-1995" data-mp="3sg3l8he9vflc">
                    {t('Continuer sans adresse', 'Continue without an address')}
                  </span>
                </span>
              </button>
            </div>}

          {/* STEP 4 — Détails */}
          {step === 4 && <div data-id="element-1996" data-mp="3k3jxhbkw1buf">
              <h3 className="text-lg font-bold text-[#0F172A] mb-4" data-id="element-1997" data-mp="3f60e5x9c28vm">
                {t('Détails de la requête', 'Request details')}
              </h3>
              <div className="mb-4" data-id="element-1998" data-mp="3b6ja989k1jsk">
                <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-1999" data-mp="bpb8edhkr370">
                  {t('Titre', 'Title')}
                </label>
                <input type="text" value={detailTitle} onChange={e => setDetailTitle(e.target.value)} placeholder={t('Titre de la requête', 'Request title')} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-2000" data-mp="6rrrt6xemebw" />
              </div>
              <div className="mb-5" data-id="element-2001" data-mp="2iidlmafdrang">
                <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-2002" data-mp="mcmoz1suqqyd">
                  {t('Description', 'Description')}
                </label>
                <textarea value={detailDesc} onChange={e => setDetailDesc(e.target.value)} rows={5} placeholder={t('Décrivez la requête…', 'Describe the request…')} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent resize-none" data-id="element-2003" data-mp="qc53nfu2y5sr" />
              </div>

              {/* Attachments */}
              <div data-id="element-2004" data-mp="vbqasdjelkgk">
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2" data-id="element-2005" data-mp="10ce2z2jdc7cd">
                  <PaperclipIcon className="w-4 h-4 text-slate-500" data-id="element-2006" data-mp="1o2i0qt9vlcvg" />
                  {t('Pièces jointes', 'Attachments')}
                </label>
                <input ref={fileInputRef} type="file" multiple className="hidden" onChange={e => handleFiles(e.target.files)} data-id="element-2007" data-mp="1sujfcluinfon" />
                <button type="button" onClick={() => fileInputRef.current?.click()} className="w-full flex flex-col items-center justify-center gap-2 px-4 py-8 border-2 border-dashed border-slate-200 rounded-xl text-slate-500 hover:border-[#4F46E5] hover:bg-indigo-50/30 transition-colors" data-id="element-2008" data-mp="3mbnxgbgte872">
                  <UploadCloudIcon className="w-7 h-7 text-slate-400" data-id="element-2009" data-mp="2pmzmfz3inl75" />
                  <span className="text-sm font-semibold text-slate-700" data-id="element-2010" data-mp="33m8ycmkovt5b">
                    {t('Cliquez pour téléverser des fichiers', 'Click to upload files')}
                  </span>
                  <span className="text-xs text-slate-400" data-id="element-2011" data-mp="iu5sqcwc9hav">
                    {t("Photos, PDF ou documents — jusqu'à 10 Mo par fichier", 'Photos, PDFs or documents — up to 10 MB per file')}
                  </span>
                </button>

                {attachments.length > 0 && <ul className="mt-3 space-y-2" data-id="element-2012" data-mp="ogbwr6lnjiev">
                    {attachments.map((file, i) => <li key={`${file.name}-${i}`} className="flex items-center gap-3 px-3 py-2.5 border border-slate-200 rounded-lg" data-id="element-2013" data-mp="jh8q5hq0x44s" data-mpi={i}>
                        <FileIcon className="w-5 h-5 text-[#4F46E5] flex-shrink-0" data-id="element-2014" data-mp="1c6pfg6ick4ws" />
                        <div className="min-w-0 flex-1" data-id="element-2015" data-mp="176tao51ts9g6">
                          <p className="text-sm font-medium text-slate-800 truncate" data-id="element-2016" data-mp="179ftj0u62bdp">
                            {file.name}
                          </p>
                          <p className="text-xs text-slate-400" data-id="element-2017" data-mp="xibbfs972aen">
                            {formatSize(file.size)}
                          </p>
                        </div>
                        <button type="button" onClick={() => removeAttachment(i)} className="text-slate-400 hover:text-red-600 flex-shrink-0" aria-label={`${t('Retirer', 'Remove')} ${file.name}`} data-id="element-2018" data-mp="1q6gy4kjt69h4">
                          <XIcon className="w-4 h-4" data-id="element-2019" data-mp="27ehwcd22wccv" />
                        </button>
                      </li>)}
                  </ul>}
              </div>
            </div>}
        </div>

        {/* Footer */}
        <div className="px-8 py-5 border-t border-slate-100 flex items-center justify-between flex-shrink-0" data-id="element-2020" data-mp="2lfaur8vi3rl9">
          <button onClick={prev} className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${step === 0 ? 'invisible' : 'text-slate-600 hover:bg-slate-100'}`} data-id="element-2021" data-mp="2q6aroe4me9nr">
            <ChevronLeftIcon className="w-4 h-4" data-id="element-2022" data-mp="305i1wsg25o6n" /> {t('Précédent', 'Previous')}
          </button>
          <button onClick={next} disabled={!canContinue()} className="flex items-center gap-1.5 px-5 py-2.5 text-sm font-semibold text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-2023" data-mp="346lijh5xjo8x">
            {step === STEPS.length - 1 ? t('Créer la requête', 'Create request') : t('Suivant', 'Next')}
            {step < STEPS.length - 1 && <ChevronRightIcon className="w-4 h-4" data-id="element-2024" data-mp="1nr0cevoth502" />}
          </button>
        </div>
      </div>
    </div>;
}